/*
Purpose: Every write the Calendar (v2) screen performs, with its toast and refetch.
*/

import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { ENROL_CATEGORY, FORFEIT_TYPE, TOAST_AUTO_CLOSE_MS } from './constants';
import * as api from './calendar-api';

const TOAST_OPTIONS = { autoClose: TOAST_AUTO_CLOSE_MS };

/** Stable identifier for an in-flight action, e.g. `"42:defer"`. */
export function buildActionKey(scopeKey, action) {
  return `${scopeKey}:${action}`;
}

/**
 * @param {Object} handlers
 * @param {Function} handlers.onSessionsChanged Refetch the calendar grid.
 * @param {Function} handlers.onInboxesChanged  Refetch the four request inboxes.
 * @param {Function} handlers.onNotesChanged    Refetch the notes list.
 */
export default function useCalendarActions({
  onSessionsChanged,
  onInboxesChanged,
  onNotesChanged,
}) {
  const [busyKey, setBusyKey] = useState(null);

  /**
   * Run one mutation: hold the busy flag, toast the outcome, then refetch.
   */
  const execute = useCallback(
    async ({ key, request, successMessage, failureMessage, refetch }) => {
      setBusyKey(key);
      try {
        const result = await request();
        const ok = result === true || result?.ok === true;

        if (ok) {
          toast.success(result?.message || successMessage, TOAST_OPTIONS);
          if (refetch) {
            await refetch();
          }
          return true;
        }

        toast.error(result?.message || failureMessage, TOAST_OPTIONS);
        return false;
      } catch (error) {
        console.error(`Calendar: action "${key}" failed`, error);
        toast.error(failureMessage, TOAST_OPTIONS);
        return false;
      } finally {
        setBusyKey(null);
      }
    },
    []
  );

  const refreshSessions = useCallback(() => onSessionsChanged(), [onSessionsChanged]);

  const refreshSessionsAndInboxes = useCallback(
    () => Promise.all([onSessionsChanged(), onInboxesChanged()]),
    [onSessionsChanged, onInboxesChanged]
  );

  /* ---------------------------------------------------------------- */
  /* Per-student actions                                              */
  /* ---------------------------------------------------------------- */

  const saveAttendanceStatus = useCallback(
    (student, status) =>
      execute({
        key: buildActionKey(student.key, 'status'),
        request: () =>
          api.updateAttendanceStatus({
            attendanceKey: student.raw.attendance_key,
            status,
          }),
        successMessage: 'Success! Attendance status has been changed.',
        failureMessage: 'Failure! Unable to update status. Try again later.',
        refetch: refreshSessions,
      }),
    [execute, refreshSessions]
  );

  const saveEnrolCategory = useCallback(
    (student, enrolCategory) =>
      execute({
        key: buildActionKey(student.key, 'mode'),
        request: () =>
          api.updateEnrolCategory({
            studentKey: student.raw.fk_stu_key,
            sessionKey: student.raw.seskey,
            attendanceKey: student.raw.attendance_key,
            // The endpoint clears a per-session override when the value is unchanged.
            enrolCategory: enrolCategory === student.enrolCategory ? null : enrolCategory,
          }),
        successMessage: 'Success! Enrolment category has been changed.',
        failureMessage: 'Failure! Unable to update the enrolment category. Try again later.',
        refetch: refreshSessions,
      }),
    [execute, refreshSessions]
  );

  /** Flip one student between In Centre and Online with no confirmation dialog. */
  const toggleStudentMode = useCallback(
    (student) => {
      const next =
        student.enrolCategory === ENROL_CATEGORY.ONLINE
          ? ENROL_CATEGORY.IN_CENTRE
          : ENROL_CATEGORY.ONLINE;
      return saveEnrolCategory(student, next);
    },
    [saveEnrolCategory]
  );

  /**
   * Apply In Centre / Online to every student in the session that is not already on
   * that category. Runs the updates in parallel, then refetches once.
   */
  const setSessionEnrolCategory = useCallback(
    (session, enrolCategory) =>
      execute({
        key: buildActionKey(session.key, 'sessionMode'),
        request: async () => {
          const targets = session.students.filter(
            (student) => student.enrolCategory !== enrolCategory
          );
          if (targets.length === 0) {
            return true;
          }

          const results = await Promise.all(
            targets.map((student) =>
              api.updateEnrolCategory({
                studentKey: student.raw.fk_stu_key,
                sessionKey: student.raw.seskey,
                attendanceKey: student.raw.attendance_key,
                enrolCategory,
              })
            )
          );

          return results.every(Boolean);
        },
        successMessage: `Success! Session mode set to ${enrolCategory}.`,
        failureMessage: 'Failure! Unable to update the session mode. Try again later.',
        refetch: refreshSessions,
      }),
    [execute, refreshSessions]
  );

  /** Check/uncheck Flexi for every student in the session. */
  const setSessionFlexi = useCallback(
    (session, enableFlexi) =>
      execute({
        key: buildActionKey(session.key, 'sessionFlexi'),
        request: async () => {
          const nextIsFlexi = enableFlexi ? 1 : 0;
          const targets = session.students.filter(
            (student) => student.isFlexi !== enableFlexi
          );
          if (targets.length === 0) {
            return true;
          }

          const results = await Promise.all(
            targets.map(async (student) => {
              const { ok } = await api.setFlexiSession({
                sessionKey:
                  student.raw.pk_ses_key || student.raw.fk_ses_key || session.sessionKey,
                studentKey: student.raw.pk_student_key || student.raw.fk_stu_key,
                isFlexi: nextIsFlexi,
              });
              return ok;
            })
          );

          return results.every(Boolean);
        },
        successMessage: enableFlexi
          ? 'Success! Flexi has been enabled for this session.'
          : 'Success! Flexi has been removed from this session.',
        failureMessage: 'Failure! Unable to update Flexi for this session. Try again later.',
        refetch: refreshSessions,
      }),
    [execute, refreshSessions]
  );

  const deferStudent = useCallback(
    (student) =>
      execute({
        key: buildActionKey(student.key, 'defer'),
        request: () =>
          api.deferStudentSession({
            studentKey: student.raw.fk_stu_key,
            sessionTemplateKey: student.raw.fk_sest_key,
            sessionKey: student.raw.seskey,
            enrolKey: student.raw.pk_enrol_key,
          }),
        successMessage: 'Success! Defer request has been raised.',
        failureMessage: 'Failure! Unable to raise the defer request. Try again later.',
        refetch: refreshSessionsAndInboxes,
      }),
    [execute, refreshSessionsAndInboxes]
  );

  const requestOneOff = useCallback(
    (student) =>
      execute({
        key: buildActionKey(student.key, 'oneoff'),
        request: () =>
          api.createOneOffSession({
            studentKey: student.raw.fk_stu_key,
            sessionTemplateKey: student.raw.fk_sest_key,
            sessionKey: student.raw.seskey,
            enrolKey: student.raw.pk_enrol_key,
          }),
        successMessage: 'Success! One-off session has been requested.',
        failureMessage: 'Failure! Unable to request a one-off session. Try again later.',
        refetch: refreshSessionsAndInboxes,
      }),
    [execute, refreshSessionsAndInboxes]
  );

  const toggleFlexi = useCallback(
    (session, student) => {
      const nextIsFlexi = student.isFlexi ? 0 : 1;

      return execute({
        key: buildActionKey(student.key, 'flexi'),
        request: async () => {
          const { ok, notFound } = await api.setFlexiSession({
            // `pk_ses_key` sits on the student row for most payloads but falls back to the
            // parent session for older records.
            sessionKey: student.raw.pk_ses_key || student.raw.fk_ses_key || session.sessionKey,
            studentKey: student.raw.pk_student_key || student.raw.fk_stu_key,
            isFlexi: nextIsFlexi,
          });

          if (ok) {
            return true;
          }
          return {
            ok: false,
            message: notFound ? 'Failure! Attendance record not found.' : null,
          };
        },
        successMessage: `Success! Session has been ${nextIsFlexi === 1 ? 'set as flexi' : 'removed from flexi'
          } successfully.`,
        failureMessage: 'Failure! Unable to update the flexi session. Try again later.',
        refetch: refreshSessions,
      });
    },
    [execute, refreshSessions]
  );

  const pauseStudent = useCallback(
    (student, session, pauseForm) =>
      execute({
        key: buildActionKey(student.key, 'pause'),
        request: async () => {
          const result = await api.createStudentPause({
            studentKey: student.raw.fk_stu_key,
            enrolKey: student.raw.pk_enrol_key,
            sessionTemplateKey: student.raw.fk_sest_key || session.sessionTemplateKey,
            pauseStartDate: pauseForm.pauseStartDate,
            pauseEndDate: pauseForm.pauseEndDate,
            pauseDescription: pauseForm.pauseDescription,
            pauseApplyType: pauseForm.pauseApplyType,
          });

          if (result.ok) {
            return true;
          }
          return { ok: false, message: result.message };
        },
        successMessage: 'Success! Student session(s) have been paused successfully.',
        failureMessage: 'Failure! Unable to process pause. Try again later.',
        refetch: refreshSessions,
      }),
    [execute, refreshSessions]
  );

  const reEnrolStudent = useCallback(
    (student, targetSession, reEnrolDate) =>
      execute({
        key: buildActionKey(student.key, 'reenrol'),
        request: () =>
          api.reEnrolStudent({
            enrolKey: student.raw.pk_enrol_key,
            sessionTemplateKey: student.raw.fk_sest_key,
            studentKey: student.raw.fk_stu_key,
            targetSessionTemplateKey: targetSession.pk_sest_key,
            sessionDate: student.raw.sdate,
            reEnrolDate,
          }),
        successMessage: 'Success! Student has been re-enrolled successfully.',
        failureMessage: 'Failure! Unable to process the re-enrolment. Try again later.',
        refetch: refreshSessionsAndInboxes,
      }),
    [execute, refreshSessionsAndInboxes]
  );

  /* ---------------------------------------------------------------- */
  /* Inbox actions                                                    */
  /* ---------------------------------------------------------------- */

  const approveDeferRequest = useCallback(
    (record) =>
      execute({
        key: buildActionKey(record.cancel_session_key, 'approve'),
        request: () =>
          api.approveDeferRequest({
            cancelSessionKey: record.cancel_session_key,
            studentKey: record.fk_student_key,
            sessionKey: record.fk_ses_key,
          }),
        successMessage: 'Success! Approved the defer request.',
        failureMessage: 'Failure! Unable to approve the defer request. Try again later.',
        refetch: refreshSessionsAndInboxes,
      }),
    [execute, refreshSessionsAndInboxes]
  );

  const disapproveDeferRequest = useCallback(
    (record) =>
      execute({
        key: buildActionKey(record.cancel_session_key, 'disapprove'),
        request: () =>
          api.disapproveDeferRequest({
            cancelSessionKey: record.cancel_session_key,
            studentKey: record.fk_student_key,
            sessionKey: record.fk_ses_key,
          }),
        successMessage: 'Success! Disapproved the defer request.',
        failureMessage: 'Failure! Unable to disapprove the defer request. Try again later.',
        refetch: refreshSessionsAndInboxes,
      }),
    [execute, refreshSessionsAndInboxes]
  );

  const settleDeferredSession = useCallback(
    (record, forfeitType) => {
      const isForfeit = forfeitType === FORFEIT_TYPE.FORFEIT;

      return execute({
        key: buildActionKey(record.cancel_session_key, isForfeit ? 'forfeit' : 'writeoff'),
        request: async () => {
          await api.settleDeferredSession({ record, forfeitType });
          // This endpoint returns no success flag, so a resolved request is the signal.
          return true;
        },
        successMessage: `Success! Session has been ${isForfeit ? 'forfeit' : 'written off'
          } successfully.`,
        failureMessage: 'Failure! Unable to process the forfeit or write-off. Try again later.',
        refetch: refreshSessionsAndInboxes,
      });
    },
    [execute, refreshSessionsAndInboxes]
  );

  const voidDeferment = useCallback(
    (record) =>
      execute({
        key: buildActionKey(record.cancel_session_key, 'void'),
        request: async () => {
          const { ok, reason } = await api.voidDeferment(record);
          if (ok) {
            return true;
          }

          const reasonMessages = {
            RECORD_NOT_FOUND: 'Failure! Deferment record not found.',
            INVALID_STATUS: 'Failure! This deferment can no longer be voided.',
          };
          return { ok: false, message: reasonMessages[reason] || null };
        },
        successMessage: 'Success! Deferment has been voided.',
        failureMessage: 'Failure! Unable to void the deferment. Try again later.',
        refetch: refreshSessionsAndInboxes,
      }),
    [execute, refreshSessionsAndInboxes]
  );

  const assignSessionsToSlot = useCallback(
    (session, records) =>
      execute({
        key: buildActionKey(session.key, 'assign'),
        request: () => api.assignSessionsToSlot({ session, records }),
        successMessage: `Success! ${records.length} lesson${records.length === 1 ? '' : 's'
          } assigned to this session.`,
        failureMessage: 'Failure! Unable to assign the selected lessons. Try again later.',
        refetch: refreshSessionsAndInboxes,
      }),
    [execute, refreshSessionsAndInboxes]
  );

  /* ---------------------------------------------------------------- */
  /* Session and note actions                                         */
  /* ---------------------------------------------------------------- */

  const rescheduleSession = useCallback(
    (session, values) =>
      execute({
        key: buildActionKey(session.key, 'reschedule'),
        request: async () => {
          const { ok, tutorUnavailable } = await api.updateSessionDateTime({
            sessionKey: session.sessionKey,
            ...values,
          });

          if (ok) {
            return true;
          }
          return {
            ok: false,
            message: tutorUnavailable
              ? 'Failure! This tutor already has a session at that date and time.'
              : null,
          };
        },
        successMessage: 'Success! Session has been updated successfully.',
        failureMessage: 'Failure! Unable to update the session. Try again later.',
        refetch: refreshSessions,
      }),
    [execute, refreshSessions]
  );

  const addNote = useCallback(
    (noteText) =>
      execute({
        key: buildActionKey('note', 'create'),
        request: () => api.createNote(noteText),
        successMessage: 'Success! Your note has been added.',
        failureMessage: 'Failure! Unable to create the note. Try again later.',
        refetch: onNotesChanged,
      }),
    [execute, onNotesChanged]
  );

  const removeNote = useCallback(
    (noteKey) =>
      execute({
        key: buildActionKey(noteKey, 'deleteNote'),
        request: async () => {
          await api.deleteNote(noteKey);
          // As with forfeit, this endpoint reports no status of its own.
          return true;
        },
        successMessage: 'Success! Your note has been deleted.',
        failureMessage: 'Failure! Unable to delete the note. Try again later.',
        refetch: onNotesChanged,
      }),
    [execute, onNotesChanged]
  );

  return {
    busyKey,
    isBusy: busyKey !== null,
    saveAttendanceStatus,
    saveEnrolCategory,
    toggleStudentMode,
    setSessionEnrolCategory,
    setSessionFlexi,
    deferStudent,
    requestOneOff,
    toggleFlexi,
    pauseStudent,
    reEnrolStudent,
    approveDeferRequest,
    disapproveDeferRequest,
    settleDeferredSession,
    voidDeferment,
    assignSessionsToSlot,
    rescheduleSession,
    addNote,
    removeNote,
  };
}
